import { BrowserRouter } from 'react-router-dom';
import { LocalizationProvider as LocalizationProviderPro } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns as AdapterDateFnsPro } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { SnackbarProvider } from 'notistack';

import { el } from 'date-fns/locale/el';
import { enUS } from 'date-fns/locale/en-US';
import { setDefaultOptions } from 'date-fns';
import type { Locale } from 'date-fns';

import AppRoutes from './routes';
import { BASE_URL } from 'config';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useEffect, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { useInitializeUser } from 'hooks/useInitializeUser';
import { useTranslation } from 'react-i18next';
import ServiceUnavailable from 'components/ServiceUnavailable';

import 'react-big-calendar/lib/css/react-big-calendar.css';

const dateFnsLocales: Record<string, Locale> = { el, en: enUS };

const App = () => {
  const auth = useAuth();
  const [hasTriedSignIn, setHasTriedSignIn] = useState(false);
  const { i18n } = useTranslation();
  const fallbackLng = ([] as string[]).concat(i18n.options.fallbackLng as string | string[])[0];
  const activeLocale = dateFnsLocales[i18n.language] ?? dateFnsLocales[fallbackLng];

  useEffect(() => {
    setDefaultOptions({ locale: activeLocale });
    document.documentElement.lang = i18n.language;
  }, [activeLocale, i18n.language]);

  const userConfigLoaded = useInitializeUser(auth.isAuthenticated);

  useEffect(() => {
    if (!hasTriedSignIn && !hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      auth.signinRedirect();
      setHasTriedSignIn(true);
    }
  }, [auth, hasTriedSignIn]);

  if (auth.isLoading) {
    return (
      <Backdrop open style={{ zIndex: 9999, color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (auth.error?.message === 'Failed to fetch') {
    return <ServiceUnavailable error={auth.error} />;
  }

  if (!userConfigLoaded) {
    return (
      <Backdrop open style={{ zIndex: 9999, color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <>
      <BrowserRouter basename={BASE_URL}>
        <LocalizationProviderPro dateAdapter={AdapterDateFnsPro} adapterLocale={activeLocale}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={activeLocale}>
            <SnackbarProvider anchorOrigin={{ horizontal: 'right', vertical: 'top' }} maxSnack={5} dense>
              <AppRoutes />
            </SnackbarProvider>
          </LocalizationProvider>
        </LocalizationProviderPro>
      </BrowserRouter>
    </>
  );
};

export default App;
